import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilm, faUsers, faAddressCard, faTrophy, faHandshake, faChartLine, faSearch, faChartBar, faFilter, faComments, faLock, faShieldAlt, faList, faEnvelope, faTicketAlt
} from "@fortawesome/free-solid-svg-icons";
import LandingSectionHeading from "./LandingSectionHeading";
import useLandingTheme from "./useLandingTheme";
import useMediaQuery from "../../hooks/useMediaQuery";
import { AwardMockup, CatalogueMockup, FilterMockup, InboxMockup, MediaMockup, RecommendMockup, ResourceMockup } from "./LandingMockups";

import epkCover from "../../images/landing_page_redo/EpkCoverBanner.webp";
import epkReach from "../../images/landing_page_redo/EpkAudience.webp";
import epkCast from "../../images/landing_page_redo/EpkDetailsCast.webp";

import actorPortfolio from "../../images/landing_page_redo/ActorDetails.webp";
import actorGallery from "../../images/landing_page_redo/ActorPhotoGallery.webp";
import actorSocial from "../../images/landing_page_redo/ActorSocialNumbers.webp";

import distCalc from "../../images/landing_page_redo/DistributorInvestorCalculator.webp";

import salesReview from "../../images/landing_page_redo/SalesAgentSynopsis.webp";
import salesChat from "../../images/landing_page_redo/SalesAgentMessage.webp";
import salesShare from "../../images/landing_page_redo/SalesAgentShareModal.webp";

import invDetails from "../../images/landing_page_redo/InvestorEpkInfo.webp";
import invAccess from "../../images/landing_page_redo/InvestorPrivateAccessRequest.webp";
import invAccessModal from "../../images/landing_page_redo/InvestorPrivateAccessRequestModal.webp";

// Intrinsic sizes let the browser reserve space before each screenshot loads.
// Entries with a `Mockup` render a code-built screen instead of an image.
const SHOTS = {
  epkCover: { src: epkCover, width: 1600, height: 750 },
  epkReach: { src: epkReach, width: 1600, height: 104 },
  epkCast: { src: epkCast, width: 1600, height: 838 },
  epkResource: { id: "epkResource", Mockup: ResourceMockup },
  epkAwards: { id: "epkAwards", Mockup: AwardMockup },
  epkMedia: { id: "epkMedia", Mockup: MediaMockup },
  actorPortfolio: { src: actorPortfolio, width: 1600, height: 292 },
  actorGallery: { src: actorGallery, width: 1600, height: 696 },
  actorEndorse: { id: "actorEndorse", Mockup: RecommendMockup },
  actorSocial: { src: actorSocial, width: 1600, height: 84 },
  distFilter: { id: "distFilter", Mockup: FilterMockup },
  distCalc: { src: distCalc, width: 1600, height: 390 },
  salesReview: { src: salesReview, width: 1600, height: 1062 },
  salesChat: { src: salesChat, width: 896, height: 788 },
  salesShare: { src: salesShare, width: 762, height: 1030 },
  invDetails: { src: invDetails, width: 1600, height: 352 },
  invAccess: { src: invAccess, width: 1600, height: 422 },
  invAccessModal: { src: invAccessModal, width: 896, height: 800 },
  festQueue: { id: "festQueue", Mockup: CatalogueMockup },
  festMsg: { id: "festMsg", Mockup: InboxMockup },
};

const ROLES = ["Filmmakers", "Actors", "Distributors", "Sales Agents", "Investors", "Film Festivals"];

const roleSlug = (role) => role.toLowerCase().replace(/\s+/g, "-");

const getFeaturesContent = (role, t) => {
  if (role === "Filmmakers") {
    return [
      { id: 0, title: t("Stunning Visual Presentation"), description: t("Hook investors instantly with high-resolution Covers, Banners, and dedicated Photo & Video Galleries."), shots: [SHOTS.epkCover, SHOTS.epkMedia], icon: faFilm },
      { id: 1, title: t("Total Audience Reach"), description: t("Prove your film's marketability. KinoKlik automatically aggregates the combined social follower counts of your entire cast and crew."), shots: [SHOTS.epkReach], icon: faUsers },
      { id: 2, title: t("Cast, Crew & Project Details"), description: t("Build out comprehensive project details and highlight your team with dedicated, interconnected Actor and Crew profiles."), shots: [SHOTS.epkCast], icon: faAddressCard },
      { id: 3, title: t("Awards & Resource Requests"), description: t("Display your festival reviews and post public calls for specific production resources you need to finish your film."), shots: [SHOTS.epkAwards, SHOTS.epkResource], icon: faTrophy }
    ];
  }
  if (role === "Actors") {
    return [
      { id: 0, title: t("Digital Portfolio"), description: t("Showcase your range. Combine high-resolution headshots, your latest demo reel, and key statistics into a professional, shareable profile."), shots: [SHOTS.actorPortfolio, SHOTS.actorGallery], icon: faAddressCard },
      { id: 1, title: t("Industry Endorsements"), description: t("Build your reputation. Allow industry professionals, directors, and peers to officially recommend you on your profile."), shots: [SHOTS.actorEndorse], icon: faHandshake },
      { id: 2, title: t("Social Proof"), description: t("Demonstrate your built-in audience. Automatically aggregate your follower counts across Instagram, TikTok, and X."), shots: [SHOTS.actorSocial], icon: faChartLine }
    ];
  }
  if (role === "Distributors") {
    return [
      { id: 0, title: t("Deep Catalog Filtering"), description: t("Find exactly what you need. Use deep filtering tools to discover specific projects and talent based on location, demographics, and roles."), shots: [SHOTS.distFilter], icon: faSearch },
      { id: 1, title: t("Sales & Territory Calculator"), description: t("Run the numbers. Use the built-in Sales Calculator to estimate distribution revenue, territories, and potential ROI directly within the EPK."), shots: [SHOTS.distCalc], icon: faChartBar },
      { id: 2, title: t("Audience Analytics"), description: t("Evaluate a film's built-in fan base. Instantly view the combined social media reach of the cast and track the overall engagement of the EPK."), shots: [SHOTS.epkReach], icon: faUsers }
    ];
  }
  if (role === "Sales Agents") {
    return [
      { id: 0, title: t("Professional EPK Review"), description: t("Evaluate projects efficiently. Browse comprehensive Electronic Press Kits to review a film's logline, synopsis, cast, and high-resolution trailer."), shots: [SHOTS.salesReview], icon: faFilter },
      { id: 1, title: t("Direct Industry Messaging"), description: t("Negotiate directly. Use the secure, real-time messaging platform to contact filmmakers and discuss representation without leaving the app."), shots: [SHOTS.salesChat], icon: faComments },
      { id: 2, title: t("Instant Project Sharing"), description: t("Distribute your slate efficiently. Use the integrated sharing tools to instantly send polished, interactive Electronic Press Kits directly to international buyers."), shots: [SHOTS.salesShare], icon: faEnvelope }
    ];
  }
  if (role === "Investors") {
    return [
      { id: 0, title: t("Comprehensive Project Details"), description: t("Invest with clarity. Review a project's complete breakdown, including the director's vision, secured cast, and production details in one place."), shots: [SHOTS.invDetails], icon: faLock },
      { id: 1, title: t("Private Access Requests"), description: t("Access exclusive materials. Securely request access from filmmakers to read private, detailed long-synopses and sensitive project information."), shots: [SHOTS.invAccess, SHOTS.invAccessModal], icon: faComments },
      { id: 2, title: t("Built-in Audience Metrics"), description: t("Assess market potential. Review the attached cast's combined social media following to ensure the project has a built-in audience before you invest."), shots: [SHOTS.epkReach], icon: faShieldAlt }
    ];
  }
  // Film Festivals
  return [
    { id: 0, title: t("Standardized Evaluation"), description: t("Discover fresh cinematic voices. Review standardized, high-quality EPKs to easily evaluate a film's production quality and narrative."), shots: [SHOTS.festQueue], icon: faList },
    { id: 1, title: t("Direct Communication"), description: t("Connect with creators. Reach out to directors and producers directly through the built-in messaging system to discuss their projects."), shots: [SHOTS.festMsg], icon: faEnvelope },
    { id: 2, title: t("Evaluate Audience Draw"), description: t("Program films that draw a crowd. Identify projects with strong cast followings that can help drive engagement and ticket sales for your festival."), shots: [SHOTS.epkReach], icon: faTicketAlt }
  ];
};

// Screenshots or mockups for one feature, in a frame that stays dark in both themes.
function FeatureShots({ feature }) {
  const { t } = useTranslation();

  return (
    <div
      key={feature.title}
      className="kk-dark-section tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-rounded-2xl tw-border tw-border-solid tw-border-white/25 tw-bg-[#14002A] tw-p-4 motion-safe:tw-animate-fade-in sm:tw-p-6 lg:tw-min-h-[460px]"
    >
      {feature.shots.map((shot, idx) => {
        const label = `${feature.title} ${t("view")} ${idx + 1}`;
        if (shot.Mockup) return <shot.Mockup key={shot.id} label={label} />;
        return (
          <img
            key={shot.src}
            src={shot.src}
            width={shot.width}
            height={shot.height}
            alt={label}
            loading="lazy"
            decoding="async"
            className="tw-h-auto tw-max-h-[360px] tw-w-auto tw-max-w-full tw-rounded-lg tw-object-contain"
          />
        );
      })}
    </div>
  );
}

const LandingIntroductions = () => {
  const { t } = useTranslation();
  const { isLight, focusRing, accentFill } = useLandingTheme();
  // One FeatureShots at a time: under the active feature below lg, beside the list from lg.
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [activeRole, setActiveRole] = useState(ROLES[0]);
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  const tabRefs = useRef([]);

  const features = getFeaturesContent(activeRole, t);
  const activeFeature = features[activeFeatureIndex] ?? features[0];

  const selectRole = (role) => {
    setActiveRole(role);
    setActiveFeatureIndex(0);
  };

  // Arrow keys move between tabs (WAI-ARIA tabs pattern, automatic activation).
  const handleTabKeyDown = (event, index) => {
    const last = ROLES.length - 1;
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    selectRole(ROLES[next]);
    tabRefs.current[next]?.focus();
  };

  return (
    <section
      aria-labelledby="landing-features-title"
      className="tw-border-0 tw-border-t tw-border-solid tw-border-white/10 tw-bg-[#09010f] tw-px-4 tw-py-20 sm:tw-px-6 md:tw-py-28"
    >
      <div className="tw-mx-auto tw-max-w-7xl">
        <LandingSectionHeading
          id="landing-features-title"
          eyebrow={t("Built for the whole industry")}
          title={t("The modern standard for Electronic Press Kits.")}
          subtitle={t("Say goodbye to scattered Google Drive folders and bulky PDF attachments.")}
        />

        <div
          role="tablist"
          aria-label={t("Explore features for:")}
          className="tw--mx-4 tw-mt-10 tw-flex tw-gap-2 tw-overflow-x-auto tw-px-4 tw-pb-2 md:tw-mx-0 md:tw-flex-wrap md:tw-justify-center md:tw-overflow-visible md:tw-px-0"
        >
          {ROLES.map((role, index) => {
            const selected = role === activeRole;
            return (
              <button
                key={role}
                ref={(el) => { tabRefs.current[index] = el; }}
                id={`landing-role-tab-${roleSlug(role)}`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls="landing-role-panel"
                tabIndex={selected ? 0 : -1}
                onClick={() => selectRole(role)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                className={`tw-min-h-[44px] tw-shrink-0 tw-rounded-full tw-border tw-border-solid tw-px-5 tw-text-sm tw-font-bold tw-transition-colors tw-duration-200 ${focusRing} ${
                  selected
                    ? `${accentFill} tw-border-transparent`
                    : "tw-border-white/10 tw-bg-white/5 tw-text-white/70 hover:tw-bg-white/10"
                }`}
              >
                {t(role)}
              </button>
            );
          })}
        </div>

        <div
          id="landing-role-panel"
          role="tabpanel"
          aria-labelledby={`landing-role-tab-${roleSlug(activeRole)}`}
          className="tw-mt-10 tw-grid tw-gap-8 lg:tw-grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:tw-items-start"
        >
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">
            {features.map((feature, index) => {
              const active = index === activeFeatureIndex;
              return (
                <li key={`${activeRole}-${feature.id}`}>
                  <button
                    type="button"
                    aria-pressed={active}
                    onClick={() => setActiveFeatureIndex(index)}
                    className={`tw-flex tw-w-full tw-items-start tw-gap-4 tw-rounded-2xl tw-border tw-border-solid tw-p-5 tw-text-left tw-transition-colors tw-duration-200 ${focusRing} ${
                      active
                        ? `tw-bg-white/5 ${isLight ? "tw-border-[#712CB0]" : "tw-border-[#FF43A7]/50"}`
                        : "tw-border-white/10 tw-bg-transparent hover:tw-bg-white/5"
                    }`}
                  >
                    <span className="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#FF43A7]/15">
                      <FontAwesomeIcon icon={feature.icon} className="tw-text-lg tw-text-[#FF43A7]" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="tw-block tw-text-lg tw-font-bold tw-text-white md:tw-text-xl">{feature.title}</span>
                      <span className="tw-mt-1 tw-block tw-text-base tw-leading-relaxed tw-text-white/70">{feature.description}</span>
                    </span>
                  </button>

                  {/* Below lg: screenshots sit right under the selected feature */}
                  {active && !isDesktop && (
                    <div className="tw-mt-3">
                      <FeatureShots feature={feature} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {isDesktop && (
            <div className="tw-sticky tw-top-8">
              <FeatureShots feature={activeFeature} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default LandingIntroductions;
